import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SelectField } from "@/lib/crm";
import { ChevronLeft, ChevronRight, RefreshCw, Plus, CalendarX2, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, microsoftLoginUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDay } from "@/lib/format";
import { PageHeader, Panel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { EventDialog } from "@/components/calendar/EventDialog";
import { ActivityDialog } from "@/components/activities/ActivityDialog";
import { EventRow } from "@/components/calendar/EventRow";
import { WeekGrid } from "@/components/calendar/WeekGrid";
import { MonthGrid } from "@/components/calendar/MonthGrid";
import { AgendaLegend } from "@/components/calendar/AgendaLegend";
import { CaldavDialog } from "@/components/calendar/CaldavDialog";
import { isDone } from "@/lib/agendaColors";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

const VIEWS = [["day", "Giorno"], ["week", "Settimana"], ["month", "Mese"], ["list", "Elenco"]];
const VIEW_KEY = "onsuite.calendar.view";
const monthLabel = new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric" });

const mondayOf = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

const rangeFor = (view, anchor) => {
  if (view === "month") {
    const from = mondayOf(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    return [from, new Date(from.getTime() + 42 * 86400000)];
  }
  const from = mondayOf(anchor);
  return [from, new Date(from.getTime() + 7 * 86400000)];
};

const NotConnected = ({ caldav, onConnect }) => (
  <Panel className="mb-4 flex flex-col gap-3 p-4 sm:flex-row sm:items-center" data-testid="calendar-not-connected">
    <CalendarX2 className="h-6 w-6 shrink-0 text-slate-400" strokeWidth={1.75} />
    <div className="min-w-0 flex-1">
      <h2 className="font-display text-sm font-bold text-ink">{caldav ? "Calendario non collegato" : "Outlook non collegato"}</h2>
      <p className="text-xs text-slate-500">{caldav ? "Vedi attività e interventi CRM. Collega il calendario della tua casella (es. Aruba) per vedere i tuoi impegni e ricevere appuntamenti, interventi e scadenze." : "Vedi attività e interventi CRM. Collega Microsoft 365 per vedere anche i tuoi impegni Outlook e creare appuntamenti."}</p>
    </div>
    {caldav ? <Button size="sm" variant="outline" onClick={onConnect} data-testid="calendar-connect-button">Collega calendario</Button>
      : <Button asChild size="sm" variant="outline" data-testid="calendar-connect-button"><a href={microsoftLoginUrl}>Collega Outlook</a></Button>}
  </Panel>
);

const groupByDay = (items) => items.reduce((acc, e) => {
  const k = new Date(e.start_at).toDateString();
  (acc[k] = acc[k] || []).push(e);
  return acc;
}, {});

export default function CalendarPage() {
  const { can } = useAuth();
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || "week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [scope, setScope] = useState("me");
  const [cal, setCal] = useState(null);
  const [caldavOpen, setCaldavOpen] = useState(false);
  const caldav = cal?.provider === "caldav";
  const label = caldav ? "calendario" : "Outlook";
  const nav = useNavigate();
  const openItem = (it) => (it.intervention_id ? nav(`/interventi/${it.intervention_id}`) : it.kind === "activity" ? nav(`/attivita?id=${it.id}`) : setEditing(it));
  const [from, to] = useMemo(() => {
    if (view !== "day") return rangeFor(view, anchor);
    const d = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    return [d, new Date(d.getTime() + 86400000)];
  }, [view, anchor]);
  const range = { start: from.toISOString(), end: to.toISOString() };
  const changeView = (v) => { if (!v) return; setView(v); localStorage.setItem(VIEW_KEY, v); };
  const shift = (dir) => setAnchor((a) => view === "month"
    ? new Date(a.getFullYear(), a.getMonth() + dir, 1)
    : new Date(a.getTime() + dir * (view === "day" ? 1 : 7) * 86400000));
  const [creating, setCreating] = useState(null);
  const slot = (d) => ({ start_at: d.toISOString(), end_at: new Date(d.getTime() + 3600000).toISOString() });
  const newAt = can("activities:create") ? (d) => setCreating({ type: "appuntamento", outlook: !!data?.connected, ...slot(d) }) : null;
  const nextHour = () => { const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); return d; };
  const toIntervention = can("calendar:create") && data?.connected ? (v) => { setCreating(null); setEditing(slot(v ? new Date(v) : nextHour())); } : null;

  const load = useCallback(() => {
    api.get("/calendar/status").then((r) => setCal(r.data)).catch(() => {});
    api.get("/agenda", { params: { start: from.toISOString(), end: to.toISOString(), scope } })
      .then((r) => setData({ ...r.data, connected: scope !== "me" || r.data.connected !== false }))
      .catch((e) => { setData({ connected: e?.response?.status !== 409, items: [] }); toast.error(errorMessage(e)); });
  }, [from, to, scope]);
  useEffect(() => { load(); }, [load]);

  const dragged = useRef(null);
  const move = async (e, start) => {
    const end = new Date(start.getTime() + (new Date(e.end_at) - new Date(e.start_at)));
    if (start.getTime() === new Date(e.start_at).getTime()) { return; }
    const timed = e.type === "appuntamento" || e.type === "chiamata";
    try {
      if (e.kind === "activity") {
        await api.patch(`/activities/${e.id}`, timed ? { start_at: start.toISOString(), end_at: end.toISOString() } : { due_at: start.toISOString() });
      } else {
        await api.patch(`/calendar/events/${e.id}`, { subject: e.subject, location: e.location || null, company_id: e.company_id || null, start_at: start.toISOString(), end_at: end.toISOString() });
      }
      toast.success(`Spostato a ${start.toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}${e.outlook || e.kind === "outlook" ? ` · aggiornato su ${label}` : ""}`);
    } catch (err) { toast.error(errorMessage(err)); }
    load();
  };
  const drag = {
    can: (e) => !e.is_all_day && !isDone(e) && (e.kind === "activity" ? can("activities:update") : e.kind === "outlook" && scope === "me" && can("calendar:update")),
    start: (e, offset) => { dragged.current = { e, offset }; },
    active: () => !!dragged.current,
    item: () => dragged.current?.e,
    offset: () => dragged.current?.offset || 0,
    drop: (start) => { const d = dragged.current; dragged.current = null; if (d) { move(d.e, start); } },
    end: () => { dragged.current = null; },
  };

  const sync = async () => {
    setBusy(true);
    try { await api.post("/calendar/sync", null, { params: range }); load(); toast.success("Calendario sincronizzato"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const groups = groupByDay(data?.items || []);

  return (
    <div data-testid="calendar-page">
      <PageHeader overline={caldav ? "Agenda · Calendario" : "Agenda · Outlook"} title="Agenda"
        description={`Il tuo ${caldav ? "calendario" : "calendario Outlook"} e le attività CRM. Nella vista team vedi solo attività e appuntamenti CRM, mai gli impegni personali.`}
        actions={<>
          {caldav && cal?.connected && <Button variant="outline" size="sm" onClick={() => setCaldavOpen(true)} className="gap-2" title="Impostazioni calendario" data-testid="calendar-caldav-settings"><Settings2 className="h-4 w-4" />{cal.calendar_name || "Calendario"}</Button>}
          {can("activities:read") && <SelectField value={scope} onChange={setScope} options={[{ key: "me", label: "La mia agenda" }, { key: "team", label: "Team" }, { key: "all", label: "Tutti" }]} className="w-40" testId="agenda-scope" />}
          {data?.connected && scope === "me" && <Button variant="outline" onClick={sync} disabled={busy} className="gap-2" data-testid="calendar-sync-button"><RefreshCw className={busy ? "animate-spin" : ""} />Sincronizza</Button>}
          {can("activities:create") && <Button onClick={() => newAt(nextHour())} className="gap-2" data-testid="calendar-new-event-button"><Plus />Nuovo appuntamento</Button>}
        </>} />
      {data && !data.connected && <NotConnected caldav={caldav} onConnect={() => setCaldavOpen(true)} />}
      {data && (
        <Panel className="p-5">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Button size="icon" variant="ghost" onClick={() => shift(-1)} aria-label="Periodo precedente" data-testid="calendar-prev"><ChevronLeft /></Button>
            <Button size="sm" variant="outline" onClick={() => setAnchor(new Date())} data-testid="calendar-today">Oggi</Button>
            <Button size="icon" variant="ghost" onClick={() => shift(1)} aria-label="Periodo successivo" data-testid="calendar-next"><ChevronRight /></Button>
            <p className="ml-2 text-sm font-semibold capitalize text-ink" data-testid="calendar-range">
              {view === "month" ? monthLabel.format(anchor) : `${fmtDay(from)} – ${fmtDay(new Date(to.getTime() - 1))}`}
            </p>
            <ToggleGroup type="single" value={view} onValueChange={changeView} className="ml-auto rounded-control border border-line p-0.5" data-testid="calendar-view-toggle">
              {VIEWS.map(([v, l]) => (
                <ToggleGroupItem key={v} value={v} className="h-8 rounded-md px-3 text-xs font-semibold data-[state=on]:bg-brand-50 data-[state=on]:text-brand-700" data-testid={`calendar-view-${v}`}>{l}</ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
          <AgendaLegend items={data.items} />
          {view === "week" && <WeekGrid from={from} items={data.items} onOpen={openItem} onCreate={newAt} drag={drag} />}
          {view === "month" && <MonthGrid from={from} month={anchor.getMonth()} items={data.items} onOpen={openItem} onCreate={newAt} drag={drag} />}
          {(view === "list" || view === "day") && Object.keys(groups).length === 0 && <p className="py-8 text-center text-sm text-slate-500" data-testid="calendar-empty">Nessun appuntamento in questa settimana.</p>}
          {(view === "list" || view === "day") && <div className="space-y-5">
            {Object.entries(groups).map(([day, items]) => (
              <section key={day}>
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">{fmtDay(items[0].start_at)}</p>
                <ul className="divide-y divide-line">{items.map((e) => <EventRow key={e.id} e={e} onEdit={openItem} onDeleted={load} />)}</ul>
              </section>
            ))}
          </div>}
        </Panel>
      )}
      <CaldavDialog open={caldavOpen} onOpenChange={setCaldavOpen} onSaved={load} />
      <EventDialog event={editing} onClose={() => setEditing(null)} onSaved={load} />
      <ActivityDialog open={!!creating} preset={creating || undefined} onOpenChange={(o) => !o && setCreating(null)} onSaved={load} onIntervention={toIntervention} />
    </div>
  );
}
